import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/chat.css';

const ChatHeaderControls = ({
  activeTab = 'status',
  onTabChange,
  onSearch,
  disabledTabs = [],
  visibleTabs = ['status', 'job_posting', 'sourcing', 'interview', 'overview'],
  fromDashboard = false,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';

  const TabButton = ({ id, label }) => {
    if (!visibleTabs.includes(id)) return null;
    const isActive = activeTab === id;
    const isDisabled = disabledTabs.includes(id);
    const workspaceLabels = {
      status: 'Status',
      job_posting: 'Job Posting',
      sourcing: 'Sourcing',
      interview: 'Interview',
      overview: 'Overview',
    };
    if (variant === 'workspace') {
      return (
        <button
          type="button"
          onClick={() => !isDisabled && onTabChange?.(id)}
          disabled={isDisabled}
          className="chat_font_medium chat_transition flex items-center justify-center"
          style={{
            height: '30px',
            padding: '0 clamp(8px, 2vw, 16px)',
            minWidth: 'clamp(64px, 18vw, 88px)',
            border: 'none',
            outline: isActive && isDark ? `1px solid ${primaryColor}B3` : 'none',
            outlineOffset: '-1px',
            borderRadius: '6px',
            backgroundColor: isActive
              ? (isDark ? `${primaryColor}52` : '#FFFFFF')
              : 'transparent',
            color: isDisabled
              ? (isDark ? '#4B5563' : '#A6B2C3')
              : isActive && isDark ? '#FFFFFF' : (isDark ? '#CBD5E1' : '#111827'),
            fontSize: 'clamp(11px, 1.35vw, 13px)',
            fontWeight: 500,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.55 : 1,
            whiteSpace: 'nowrap',
            boxShadow: isActive
              ? (isDark ? `0 0 0 1px ${primaryColor}35, 0 4px 12px rgba(0, 0, 0, 0.28)` : '0 1px 3px rgba(15, 23, 42, 0.10)')
              : 'none',
          }}
          aria-pressed={isActive}
          title={isDisabled ? (id === 'interview' ? 'Find candidates for this job first' : 'Create a Job Description first') : ''}
        >
          <span>{workspaceLabels[id] || label}</span>
        </button>
      );
    }
    const minWidthMap = {
      job_posting: '92px',
      status: '78px',
      sourcing: '78px',
      interview: '82px',
      overview: '78px',
    };
    return (
      <button
        type="button"
        onClick={() => !isDisabled && onTabChange?.(id)}
        disabled={isDisabled}
        className="chat_font_medium chat_transition flex items-center justify-center chat_text_sm"
        style={{
          padding: '6px 14px',
          lineHeight: '20px',
          whiteSpace: 'nowrap',
          height: '32px',
          borderRadius: '6px',
          backgroundColor: isActive
            ? (isDark ? '#1F2937' : '#FFFFFF')
            : 'transparent',
          color: isDisabled
            ? (isDark ? '#4B5563' : '#9CA3AF')
            : isActive
              ? (isDark ? '#F9FAFB' : '#6B7280')
              : (isDark ? '#D1D5DB' : '#1F2937'),
          border: isActive ? 'none' : 'none',
          minWidth: minWidthMap[id] || '78px',
          boxShadow: isActive ? (isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)') : 'none',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          position: 'relative',
          zIndex: isActive ? 1 : 0,
        }}
        aria-pressed={isActive}
        title={isDisabled ? (id === 'interview' ? 'Find candidates for this job first' : 'Create a Job Description first') : ''}
      >
        {label}
      </button>
    );
  };

  if (variant === 'workspace') {
    return (
      <div className="flex items-center justify-center overflow-x-auto" style={{ minWidth: 0, width: '100%', scrollbarWidth: 'none' }}>
        <div
          className="flex items-center overflow-x-auto"
          style={{
            width: 'fit-content',
            maxWidth: '100%',
            minHeight: '36px',
            padding: '2px',
            gap: '2px',
            borderRadius: '8px',
            border: `1px solid ${isDark ? `${primaryColor}99` : primaryColor}`,
            backgroundColor: isDark ? '#111827' : '#BFCBFF',
            boxSizing: 'border-box',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          role="radiogroup"
          aria-label="Workspace view selector"
        >
          <TabButton id="status" label="Status" />
          <TabButton id="job_posting" label="Job Posting" />
          <TabButton id="sourcing" label="Sourcing" />
          <TabButton id="interview" label="Interview" />
          <TabButton id="overview" label="Overview" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2" style={{ minHeight: '44px' }}>
      {fromDashboard && (
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1 chat_font_medium chat_transition chat_text_sm"
          style={{
            padding: '6px 12px',
            lineHeight: '20px',
            height: '32px',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            color: isDark ? '#D1D5DB' : '#1F2937',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '14px' }}>←</span>
          <span>Back to dashboard</span>
        </button>
      )}
      <div
        className="flex items-center gap-1"
        style={{
          padding: '3px',
          backgroundColor: isDark ? `${primaryColor}25` : `${primaryColor}3D`,
          borderRadius: '8px',
          border: `1px solid ${isDark ? `${primaryColor}80` : primaryColor}`,
          flexShrink: 0,
        }}
        role="radiogroup"
        aria-label="View selector"
      >
        <TabButton id="status" label="Status" />
        <TabButton id="job_posting" label="Job Posting" />
        <TabButton id="sourcing" label="Sourcing" />
        <TabButton id="interview" label="Interview" />
        <TabButton id="overview" label="Overview" />
      </div>
    </div>
  );
};

export default ChatHeaderControls;
